import {ContainerEditContextDto} from './ContainerEditContextDto';
export type {ContainerEditContext} from './ContainerEditContext';
import {ContainerEditContext} from './ContainerEditContext';
import type {FavoriteState} from '../favorites/favorite-state';
import type {BulkMoveRequestDto, BulkMoveResponseDto, BulkMoveResult} from './bulk-move-selection';
import {HttpClient, HttpContext} from '@angular/common/http';
import {LOCALLY_HANDLED_API_ERROR_CODES} from '../shared/errors/local-api-error';
import {Injectable} from '@angular/core';
import {forkJoin, map, Observable, of, tap} from 'rxjs';
import {Store} from '@ngrx/store';
import {ApiResponse} from '../auth/auth.models';
import {buildApiUrl} from '../shared/api/api-url';
import {type UserContact} from '../people/people.models';
import {ReportsActions} from '../reports/store/reports.actions';

export type ContainerTreeItemDto = {
  id: string;
  canEdit: boolean;
  parentId: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  depth: number;
  hasChildren?: boolean;
  hasItems?: boolean;
  isSharedRoot?: boolean;
};

export type ContainerDeleteReason = 'HasItems' | 'HasSubLocations' | 'HasShares';
export type ContainerDeleteEligibilityDto = {
  canBeDeleted: boolean;
  hasItems: boolean;
  hasSubLocations: boolean;
  hasShares: boolean;
  reason: ContainerDeleteReason | null;
  reasons: ContainerDeleteReason[];
};
export type ContainerDeleteEligibility = {canDelete: boolean; reasons: ContainerDeleteReason[]};

export type ContainerListItemDto = FavoriteState & {
  id: string;
  canEdit: boolean;
  name: string;
  description: string | null;
  imageUrl: string | null;
  hasChildren?: boolean;
  hasItems?: boolean;
  isSharedRoot?: boolean;
};

type ContainerBreadcrumbSegmentDto = {
  id: string;
  name: string;
};

type ContainerBreadcrumbResponseDto = {
  segments: ContainerBreadcrumbSegmentDto[];
};

type ContainerTreeResponseDto = {
  items?: ContainerTreeItemDto[];
};

export type ContainerTreeItem = {
  id: string;
  canEdit: boolean;
  parentId: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  depth: number;
  hasChildren?: boolean;
  hasItems?: boolean;
  isSharedRoot: boolean;
};

export type ContainerListItem = FavoriteState & {
  id: string;
  canEdit: boolean;
  name: string;
  description: string | null;
  imageUrl: string | null;
  hasChildren?: boolean;
  hasItems?: boolean;
  isSharedRoot: boolean;
};

export type ContainerBreadcrumbSegment = {
  id: string;
  name: string;
};



export type UpdateContainerRequest = {
  description: string;
  image?: File | null;
  name: string;
  parentId?: string | null;
  allowQrCodeReassignment?: boolean;
  qrCodeToken?: string;
  removeAssignedQrCode?: boolean;
  removeImage?: boolean;
};

export type CreateContainerRequest = UpdateContainerRequest;

export type CreateNumberedSubcontainersRequest = {
  count: number;
  startNumber: number;
};

export type QrCodeAssignmentStatus = 'unassigned' | 'assignedToContainer' | 'assignedToItem';

export type ResolvedQrCode = {
  assignedItemId: string | null;
  assignedItemName: string | null;
  assignedLocationId: string | null;
  assignedLocationName: string | null;
  id: string;
  status: QrCodeAssignmentStatus;
  token: string;
};

export type ContainerShareRole = 'Viewer' | 'Editor';

export type ContainerShareUser = UserContact['contactUser'] & {
  fullName: string;
};

export type ContainerShare = {
  id: string;
  rootContainerId: string;
  rootContainerName: string;
  role: ContainerShareRole;
  sharedWithUser: ContainerShareUser;
  grantedByUserId: string;
  created: string;
  updated: string;
};

export type ContainerInheritedAccess = {
  role: ContainerShareRole;
  sharedWithUserId: string;
  sourceContainerId: string;
  sourceContainerName: string;
};

export type ContainerSharingContext = {
  explicitShares: ContainerShare[];
  inheritedAccess: ContainerInheritedAccess[];
};



type ContainerShareUserDto = {
  id: string;
  email: string;
  username: string;
  firstName: string | null;
  lastName: string | null;
};

type ContainerShareDto = {
  id: string;
  rootLocationId: string;
  rootLocationName: string;
  role: ContainerShareRole;
  sharedWithUser: ContainerShareUserDto;
  grantedByUserId: string;
  created: string;
  updated: string;
};

type UpdateContainerResponseDto = Record<string, never>;
type CreateNumberedSubcontainersRequestDto = {
  count: number;
  startNumber: number;
};
type CreateNumberedSubcontainersResponseDto = {
  createdIds: string[];
};
type QrCodeResolveResponseDto = {
  assignedItemId: string | null;
  assignedItemName: string | null;
  assignedLocationId: string | null;
  assignedLocationName: string | null;
  id: string;
  status: 'Unassigned' | 'AssignedToContainer' | 'AssignedToItem';
  token: string;
};
type CreateContainerShareRequestDto = {
  SharedWithUserId: string;
  Role: ContainerShareRole;
};
type UpdateContainerShareRequestDto = {
  Role: ContainerShareRole;
};

function formatContactName(firstName: string | null, lastName: string | null, fallback: string): string {
  return [firstName?.trim(), lastName?.trim()]
    .filter((part): part is string => Boolean(part))
    .join(' ')
    .trim() || fallback;
}

function toContainerTreeItems(payload: ApiResponse<ContainerTreeResponseDto>): ContainerTreeItem[] {
  return (payload.data?.items ?? []).map((item) => ({
    id: item.id,
    canEdit: item.canEdit,
    parentId: item.parentId,
    name: item.name,
    description: item.description,
    imageUrl: item.imageUrl,
    depth: item.depth,
    hasChildren: item.hasChildren,
    hasItems: item.hasItems,
    isSharedRoot: item.isSharedRoot ?? false,
  }));
}

function toContainerListItems(payload: ApiResponse<ContainerListItemDto[]>): ContainerListItem[] {
  return (payload.data ?? []).map((item) => ({
    id: item.id,
    isFavorite: item.isFavorite,
    canEdit: item.canEdit,
    name: item.name,
    description: item.description,
    imageUrl: item.imageUrl,
    hasChildren: item.hasChildren,
    hasItems: item.hasItems,
    isSharedRoot: item.isSharedRoot ?? false,
  }));
}

function toContainerBreadcrumb(payload: ApiResponse<ContainerBreadcrumbResponseDto>): ContainerBreadcrumbSegment[] {
  return (payload.data?.segments ?? []).map((segment) => ({
    id: segment.id,
    name: segment.name,
  }));
}

function toContainerEditContext(payload: ApiResponse<ContainerEditContextDto>): ContainerEditContext {
  const data = payload.data;
  if (!data) {
    throw new Error('Container edit context is missing.');
  }

  return {
    assignedQrCode: data.assignedQrCode,
    description: data.description,
    imageUrl: data.imageUrl,
    originalImageUrl: data.originalImageUrl ?? null,
    name: data.name,
    parentId: data.parentId,
  };
}

function toResolvedQrCode(dto: QrCodeResolveResponseDto): ResolvedQrCode {
  return {
    assignedItemId: dto.assignedItemId,
    assignedItemName: dto.assignedItemName,
    assignedLocationId: dto.assignedLocationId,
    assignedLocationName: dto.assignedLocationName,
    id: dto.id,
    status: dto.status === 'AssignedToItem'
      ? 'assignedToItem'
      : dto.status === 'AssignedToContainer'
        ? 'assignedToContainer'
        : 'unassigned',
    token: dto.token,
  };
}

function toContainerShareUser(dto: ContainerShareUserDto): ContainerShareUser {
  return {
    id: dto.id,
    email: dto.email,
    username: dto.username,
    fullName: formatContactName(dto.firstName, dto.lastName, dto.username || dto.email),
  };
}

function toContainerShare(dto: ContainerShareDto): ContainerShare {
  return {
    id: dto.id,
    rootContainerId: dto.rootLocationId,
    rootContainerName: dto.rootLocationName,
    role: dto.role,
    sharedWithUser: toContainerShareUser(dto.sharedWithUser),
    grantedByUserId: dto.grantedByUserId,
    created: dto.created,
    updated: dto.updated,
  };
}

@Injectable({
  providedIn: 'root',
})
export class ContainersService {
  constructor(
    private readonly httpClient: HttpClient,
    private readonly store: Store,
  ) {}

  resolveQrCode(token: string): Observable<ResolvedQrCode> {
    return this.httpClient.get<ApiResponse<QrCodeResolveResponseDto>>(buildApiUrl(`/v1/qr-codes/${encodeURIComponent(token)}`),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['RecordNotFound'])}).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('QR code resolve response is missing.');
        }

        return toResolvedQrCode(payload.data);
      }),
    );
  }

  getTree(): Observable<ContainerTreeItem[]> {
    return this.httpClient.get<ApiResponse<ContainerTreeResponseDto>>(buildApiUrl('/v1/item-containers/tree')).pipe(
      map((payload) => toContainerTreeItems(payload)),
    );
  }

  bulkMove(request: BulkMoveRequestDto): Observable<BulkMoveResult> {
    return this.httpClient.post<ApiResponse<BulkMoveResponseDto>>(buildApiUrl('/v1/inventory/bulk-move'), request).pipe(
      map(payload => {
        const data = payload.data;
        if (!payload.success || !data || data.operationId !== request.operationId) {
          throw new Error('Bulk move response could not be confirmed.');
        }
        return {operationId: data.operationId, movedItems: data.itemCount, movedContainers: data.containerCount};
      }),
      tap(() => this.store.dispatch(ReportsActions.inventoryMutationCompleted({entity: 'container', operation: 'moved'}))),
    );
  }

  getRootContainers(searchTerm?: string): Observable<ContainerListItem[]> {
    const query = searchTerm?.trim()
      ? `?searchTerm=${encodeURIComponent(searchTerm.trim())}`
      : '';

    return this.httpClient.get<ApiResponse<ContainerListItemDto[]>>(buildApiUrl(`/v1/item-containers${query}`)).pipe(
      map((payload) => toContainerListItems(payload)),
    );
  }

  getChildContainers(parentId: string, searchTerm?: string): Observable<ContainerListItem[]> {
    const query = searchTerm?.trim()
      ? `?searchTerm=${encodeURIComponent(searchTerm.trim())}`
      : '';

    return this.httpClient.get<ApiResponse<ContainerListItemDto[]>>(
      buildApiUrl(`/v1/item-containers/${parentId}/containers${query}`)
    ).pipe(
      map((payload) => toContainerListItems(payload)),
    );
  }

  getBreadcrumb(containerId: string, handleUnavailable = false): Observable<ContainerBreadcrumbSegment[]> {
    return this.httpClient.get<ApiResponse<ContainerBreadcrumbResponseDto>>(
      buildApiUrl(`/v1/item-containers/${encodeURIComponent(containerId)}/breadcrumb`),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, handleUnavailable ? ['RecordNotFound'] : [])}
    ).pipe(
      map((payload) => toContainerBreadcrumb(payload)),
    );
  }

  getContainerEditContext(containerId: string): Observable<ContainerEditContext> {
    return this.httpClient.get<ApiResponse<ContainerEditContextDto>>(buildApiUrl(`/v1/item-containers/${containerId}`)).pipe(
      map((payload) => toContainerEditContext(payload)),
    );
  }

  getContainerShares(containerId: string): Observable<ContainerShare[]> {
    return this.httpClient.get<ApiResponse<ContainerShareDto[]>>(buildApiUrl(`/v1/item-containers/${containerId}/shares`)).pipe(
      map((payload) => (payload.data ?? []).map(toContainerShare)),
    );
  }

  createContainer(request: CreateContainerRequest): Observable<string> {
    const formData = new FormData();
    formData.append('Name', request.name.trim());
    formData.append('Description', request.description.trim());

    if (request.parentId) {
      formData.append('ParentId', request.parentId);
    }

    if (request.image) {
      formData.append('Image', request.image);
    }

    if (request.qrCodeToken?.trim()) {
      formData.append('QrCodeToken', request.qrCodeToken.trim());
    }

    if (request.allowQrCodeReassignment) {
      formData.append('AllowQrCodeReassignment', 'true');
    }

    return this.httpClient.post<ApiResponse<{id: string}>>(
      buildApiUrl('/v1/item-containers'),
      formData,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES,
        ['VALIDATION_ERROR', 'Conflict', 'QrCodeAlreadyAssigned', 'QrCodeOwnedByAnotherUser'])},
    ).pipe(
      map((payload) => {
        const id = payload.data?.id?.trim();
        if (!id) {
          throw new Error('Created container response is missing.');
        }

        return id;
      }),
      tap((containerId) => this.dispatchMutation(containerId, 'created')),
    );
  }

  updateContainerById(containerId: string, request: UpdateContainerRequest): Observable<void> {
    const formData = new FormData();
    formData.append('Name', request.name);
    formData.append('Description', request.description);

    if (request.parentId) {
      formData.append('ParentId', request.parentId);
    }

    if (request.image) {
      formData.append('Image', request.image);
    }

    if (request.qrCodeToken) {
      formData.append('QrCodeToken', request.qrCodeToken);
    }

    if (request.allowQrCodeReassignment) {
      formData.append('AllowQrCodeReassignment', 'true');
    }

    if (request.removeAssignedQrCode) {
      formData.append('RemoveAssignedQrCode', 'true');
    }

    if (request.removeImage) {
      formData.append('RemoveImage', 'true');
    }

    return this.httpClient.put<ApiResponse<UpdateContainerResponseDto>>(
      buildApiUrl(`/v1/item-containers/${containerId}`),
      formData,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES,
        ['VALIDATION_ERROR', 'QrCodeAlreadyAssigned', 'QrCodeOwnedByAnotherUser'])},
    ).pipe(
      map(() => void 0),
      tap(() => this.dispatchMutation(containerId, 'updated')),
    );
  }

  createContainerShare(containerId: string, sharedWithUserId: string, role: ContainerShareRole): Observable<ContainerShare> {
    const requestBody: CreateContainerShareRequestDto = {
      SharedWithUserId: sharedWithUserId,
      Role: role,
    };

    return this.httpClient.post<ApiResponse<ContainerShareDto>>(
      buildApiUrl(`/v1/item-containers/${containerId}/shares`),
      requestBody,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['Conflict'])},
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Container share is missing.');
        }

        return toContainerShare(payload.data);
      }),
      tap(() => this.dispatchMutation(containerId, 'updated')),
    );
  }

  updateContainerShare(containerId: string, shareId: string, role: ContainerShareRole): Observable<ContainerShare> {
    const requestBody: UpdateContainerShareRequestDto = {
      Role: role,
    };

    return this.httpClient.put<ApiResponse<ContainerShareDto>>(
      buildApiUrl(`/v1/item-containers/${containerId}/shares/${shareId}`),
      requestBody,
    ).pipe(
      map((payload) => {
        if (!payload.data) {
          throw new Error('Container share is missing.');
        }

        return toContainerShare(payload.data);
      }),
      tap(() => this.dispatchMutation(containerId, 'updated')),
    );
  }

  deleteContainerShare(containerId: string, shareId: string): Observable<void> {
    return this.httpClient.delete<ApiResponse<Record<string, never>>>(
      buildApiUrl(`/v1/item-containers/${containerId}/shares/${shareId}`),
    ).pipe(
      map(() => void 0),
      tap(() => this.dispatchMutation(containerId, 'updated')),
    );
  }

  deleteContainerById(containerId: string): Observable<void> {
    return this.httpClient.delete<ApiResponse<Record<string, never>>>(
      buildApiUrl(`/v1/item-containers/${containerId}`),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES,
        ['CanNotDeleteItemContainerWithItems', 'CanNotDeleteItemContainerWithChildren',
          'CanNotDeleteItemContainerWithShares', 'RecordNotFound'])},
    ).pipe(
      map(() => void 0),
      tap(() => this.dispatchMutation(containerId, 'deleted')),
    );
  }

  getDeleteEligibility(containerId: string): Observable<ContainerDeleteEligibility> {
    return this.httpClient.get<ApiResponse<ContainerDeleteEligibilityDto>>(
      buildApiUrl(`/v1/item-containers/${containerId}/can-be-deleted`),
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['RecordNotFound'])},
    ).pipe(map(payload => {
      if (!payload.success || !payload.data) throw new Error('Missing deletion eligibility.');
      return {canDelete: payload.data.canBeDeleted, reasons: payload.data.reasons};
    }));
  }

  createNumberedSubcontainers(
    parentId: string,
    request: CreateNumberedSubcontainersRequest,
  ): Observable<string[]> {
    const requestBody: CreateNumberedSubcontainersRequestDto = {
      count: request.count,
      startNumber: request.startNumber,
    };

    return this.httpClient.post<ApiResponse<CreateNumberedSubcontainersResponseDto>>(
      buildApiUrl(`/v1/item-containers/${encodeURIComponent(parentId)}/containers`),
      requestBody,
      {context: new HttpContext().set(LOCALLY_HANDLED_API_ERROR_CODES, ['Conflict', 'ParentNotFound'])},
    ).pipe(
      map((payload) => {
        const createdIds = payload.data?.createdIds;
        if (!createdIds) {
          throw new Error('Created subcontainers response is missing.');
        }

        return createdIds;
      }),
      tap((createdIds) => createdIds.forEach((containerId) => this.dispatchMutation(containerId, 'created'))),
    );
  }

  getContainerSharingContext(containerId: string, ancestorContainerIds: string[]): Observable<ContainerSharingContext> {
    const uniqueAncestorIds = ancestorContainerIds.filter((ancestorId, index, values) => (
      ancestorId !== containerId && values.indexOf(ancestorId) === index
    ));
    const ancestorShares$ = uniqueAncestorIds.length > 0
      ? forkJoin(uniqueAncestorIds.map((ancestorId) => this.getContainerShares(ancestorId)))
      : of([] as ContainerShare[][]);

    return forkJoin({
      explicitShares: this.getContainerShares(containerId),
      ancestorShares: ancestorShares$,
    }).pipe(
      map(({explicitShares, ancestorShares}) => ({
        explicitShares,
        inheritedAccess: this.resolveInheritedAccess(
          ancestorShares.reduce<ContainerShare[]>((shares, group) => shares.concat(group), []),
        ),
      })),
    );
  }

  private dispatchMutation(entityId: string, operation: 'created' | 'updated' | 'deleted'): void {
    this.store.dispatch(ReportsActions.inventoryMutationCompleted({
      entity: 'container',
      entityId,
      operation,
    }));
  }

  private resolveInheritedAccess(ancestorShares: ContainerShare[]): ContainerInheritedAccess[] {
    const accessByUserId = new Map<string, ContainerInheritedAccess>();

    for (const share of ancestorShares) {
      const existing = accessByUserId.get(share.sharedWithUser.id);
      if (existing?.role === 'Editor' && share.role === 'Viewer') {
        continue;
      }

      accessByUserId.set(share.sharedWithUser.id, {
        role: share.role,
        sharedWithUserId: share.sharedWithUser.id,
        sourceContainerId: share.rootContainerId,
        sourceContainerName: share.rootContainerName,
      });
    }

    return [...accessByUserId.values()];
  }
}
